import React, { useContext, useEffect, useRef } from 'react';
import { ColumnsType } from 'antd/es/table';
import { TableProps, Pagination, theme } from 'components';
import styled from 'styled-components';
import { LayoutContext } from '../../../contexts';
import { useTableResizer } from '../../../hooks';
import { StyledTable } from '../../components';
import { useContractsContext } from './ContractsContext';

const PAGE_SIZE = 20;

const SearchTable: React.FC<
  TableProps & {
    dataSource?: readonly any[];
    columns: ColumnsType;
    loading?: boolean;
    dataCy?: string;
    totalItems: number;
  }
> = ({
  dataSource,
  columns,
  loading,
  onChange,
  onRow,
  dataCy,
  totalItems,
  ...props
}) => {
  const { transition } = useContext(LayoutContext);
  const { page, setPage } = useContractsContext();
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
    windowResizeDelay: 200,
  });

  const pageHandler = (pageNumber: number) => {
    setPage(pageNumber);
  };

  return (
    <div ref={tableWrapperRef}>
      <StyledSearchTable
        $height={tableHeight}
        $headerHeight={tableHeaderHeight}
        showSorterTooltip={false}
        loading={Boolean(loading)}
        dataSource={dataSource}
        columns={columns}
        onChange={onChange}
        onRow={onRow}
        scroll={{
          x: '100%',
          y: `calc(100% - ${tableHeaderHeight}px)`,
        }}
        pagination={false}
        style={{
          transition: `all ${transition}s`,
        }}
        data-cy={dataCy}
        {...props}
      />
      <StyledPagination
        current={page}
        defaultPageSize={PAGE_SIZE}
        onChange={pageHandler}
        total={Math.min(totalItems, 10000) || 0}
        showSizeChanger={false}
        itemRender={(tablePage, type, element: any) => ({
          ...element,
          props: {
            ...element.props,
            'data-cy': `${tablePage}-${type}-${page}`,
          },
        })}
      />
    </div>
  );
};

export default SearchTable;

const StyledSearchTable = styled(StyledTable)`
  .ant-table-filter-trigger.active .anticon-filter {
    color: #1665fc;
  }
`;

const StyledPagination = styled(Pagination)`
  border-top: 1px solid ${theme.gray3};
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
`;
